'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { EventHero } from './EventHero';
import { EventCompactHeader } from './EventCompactHeader';
import { EventNavigation } from './EventNavigation';
import { EventTimeZoneNote } from './EventTimeZoneNote';
import {
  useEventFeedsLive,
  useEventHasFeeds,
  useEventHasMerchandise,
  useEventVenueCount,
} from './EventShellContext';
import type { Database } from '@/types/supabase';
import { isEventLive } from '@/app/[locale]/event/[eventId]/libs/eventLiveStatus';
import {
  activeEventTab,
  isWideEventPath,
  listEventTabs,
  type EventTabId,
} from '@/app/[locale]/event/[eventId]/libs/eventTabs';
import { apiGet } from '@/lib/api';
import { cn } from '@/lib/utils';

type EventRow = Database['public']['Tables']['event']['Row'];

/**
 * What the shell reads, built on the server by `(tabs)/layout.tsx`. The two organiser switches
 * arrive already resolved, so `settings` and `payment_settings` never reach the client here.
 */
export type EventShellEvent = Pick<
  EventRow,
  'id' | 'name' | 'subtitle' | 'banner_image' | 'status' | 'start_date' | 'end_date'
> & {
  /** `isCompetitionHidden`: no Draws, Schedule or Matches tab. */
  hideCompetition: boolean;
  /** `hidesRegistrantsFromPublic`: only the server can say whether this viewer sees Players. */
  hidesRegistrants: boolean;
};

interface EventLayoutProps {
  event: EventShellEvent;
  children: React.ReactNode;
}

/** Tabs whose content shows times: the only places the pinned-zone note belongs. */
const TABS_WITH_TIMES: ReadonlySet<EventTabId> = new Set([
  'overview',
  'matches',
  'schedule',
  'venues',
  'watch',
]);

export function EventLayout({ event, children }: EventLayoutProps) {
  const pathname = usePathname();
  const tNav = useTranslations('Event.navigation');
  const venueCount = useEventVenueCount();
  const hasMerchandise = useEventHasMerchandise();
  const hasFeeds = useEventHasFeeds();
  const feedsLive = useEventFeedsLive();
  const activeTab = activeEventTab(pathname);
  // Computed post-mount to avoid a time-dependent hydration mismatch: both the
  // server render and the first client render see `false`, then it settles.
  const [isLive, setIsLive] = useState(false);

  // Promote the live schedule while the event is in progress.
  useEffect(() => {
    setIsLive(isEventLive(event));
  }, [event]);

  // Players tab: shown when this viewer may see the entrants. With the switch
  // off everyone may, so server and first client render agree. With it on only
  // the server can say (an entrant may, others may not), so ask — through
  // `apiGet`, which sends the viewer's Bearer token (the route is Bearer-only;
  // a bare fetch would always be answered as signed out). The shell persists
  // across tabs, so that is once per hard load. On /players itself the tab shows
  // as the current one, but that is not a yes (a refused viewer sees the private
  // notice there), so the shell asks there too. Any failure leaves the tab
  // hidden, and an answer counts only for the event it was asked about.
  const { hidesRegistrants } = event;
  const onPlayersTab = activeTab === 'players';
  const askedFor = useRef<string | null>(null);
  const [askedVerdict, setAskedVerdict] = useState<{ eventId: string; canSee: boolean } | null>(
    null,
  );
  useEffect(() => {
    if (!hidesRegistrants || askedFor.current === event.id) return;
    const eventId = event.id;
    askedFor.current = eventId;
    const settle = (canSee: boolean) =>
      setAskedVerdict((prev) => (askedFor.current === eventId ? { eventId, canSee } : prev));
    apiGet<{ canSee?: boolean } | null>(`/api/event/${eventId}/registrants-visibility`)
      .then((data) => settle(data?.canSee === true))
      .catch(() => settle(false));
  }, [event.id, hidesRegistrants]);
  const viewerMaySee = askedVerdict?.eventId === event.id && askedVerdict.canSee;
  const showPlayers = !hidesRegistrants || viewerMaySee || onPlayersTab;

  const tabs = listEventTabs({
    hideCompetition: event.hideCompetition,
    hasMerchandise,
    isLive,
    showPlayers,
    venueCount,
    hasFeeds,
    feedsLive,
  });
  const activeLabelKey = tabs.find((x) => x.id === activeTab)?.labelKey ?? activeTab;
  const isOverview = activeTab === 'overview';
  const wide = isWideEventPath(pathname);
  const mobileDenseDraw = /\/draw\/[^/]+\/?$/.test(pathname);

  // The content area dims while a tab's navigation is in flight: `EventNavigation` marks the
  // clicked tab with `data-tab-pending`, so no state is needed here.
  return (
    <div
      data-event-shell
      className="bg-background min-h-screen print:min-h-0 has-[[data-tab-pending]]:[&_[data-event-content]]:opacity-60"
    >
      {isOverview && <EventHero event={event} />}
      <div className={cn('container mx-auto', wide ? 'max-w-none' : 'max-w-6xl')}>
        {!isOverview && (
          <EventCompactHeader
            event={event}
            mobileDense={mobileDenseDraw}
            className={cn('mt-2 md:mb-5', mobileDenseDraw ? 'mb-2' : 'mb-5')}
          />
        )}
        <EventNavigation
          className={cn('mb-2', isOverview && 'mt-6')}
          tabs={tabs}
          activeTab={activeTab}
        />
        {TABS_WITH_TIMES.has(activeTab) && <EventTimeZoneNote at={event.start_date} />}

        {/* Page header: the overview owns the full event title. A sub-page is
            titled by its tab for screen readers; the compact identity header
            keeps the event name visible above the shared navigation. */}
        {isOverview ? (
          <div className="mt-4 mb-4">
            <h1 className="text-foreground text-3xl font-semibold lg:text-4xl">{event.name}</h1>
            {event.subtitle && (
              <p className="text-foreground text-base lg:text-lg">{event.subtitle}</p>
            )}
          </div>
        ) : (
          <h1 className="sr-only">
            {tNav(activeLabelKey)} – {event.name}
          </h1>
        )}

        <div data-event-content className="transition-opacity">
          {children}
        </div>
      </div>
    </div>
  );
}
